import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { useNavigate } from "react-router-dom";
import Compressor from "compressorjs";
import { v4 as uuidv4 } from "uuid";
import "./Upload.css";
import { Link } from "react-router-dom";

const SINGLE_LIMIT = 100 * 1024 * 1024; // 100 MiB
const PART_SIZE    = 20  * 1024 * 1024; // 20 MiB
const API_ROOT     = "https://purple-king-9edc.pandabitstudio.workers.dev";
const PER_PAGE     = 500;

const imageExt = ["jpg","jpeg","png","gif","webp","bmp","svg","heic","heif"];
const videoExt = ["mp4","webm","mov","mkv","avi"];
const audioExt = ["mp3","wav","aac","ogg","flac","m4a"];
const getExt   = f => (f.split(".").pop() || "").toLowerCase();

// Compress only image files and give each file a unique UUID-based name
const compressImage = file =>
  new Promise(resolve => {
    const makeUnique = (blob, originalName, mime) => {
      const base = originalName.replace(/\.[^/.]+$/, "");
      // Use extension matching mime
      let ext = (originalName.split(".").pop() || "").toLowerCase();
      if (mime === "image/webp") ext = "webp";
      else if (mime === "image/jpeg") ext = "jpg";
      else if (mime === "image/png") ext = "png";
      const uniqueName = `${Date.now()}-${uuidv4()}-${base}.${ext}`;
      return new File([blob], uniqueName, { type: mime });
    };

    if (!file.type.startsWith("image/")) {
      return resolve(makeUnique(file, file.name, file.type));
    }

    // Smart format selection
    let mimeType = file.type;
    if (file.type === "image/png") {
      mimeType = "image/png"; // or "image/png" if you want to keep PNGs always
    } else if (file.type === "image/heic" || file.type === "image/heif") {
      mimeType = "image/jpeg";
    } else if (file.type === "image/jpeg") {
      mimeType = "image/jpeg";
    } else if (file.type === "image/webp") {
      mimeType = "image/webp";
    } else {
      mimeType = "image/jpeg"; // fallback for unknowns
    }

    new Compressor(file, {
      mimeType: mimeType, // <--- FIXED
      quality: 0.98,
      maxWidth: 4048,
      maxHeight: 4048,
      convertSize: 2 * 1024 * 1024,
      useWebWorker: false,
      success(result) {
        resolve(makeUnique(result, file.name, mimeType)); // <--- FIXED
      },
      error(err) {
        console.warn("Compression failed:", err);
        resolve(makeUnique(file, file.name, file.type));
      },
    });
  });

export default function Upload() {
  const pw  = sessionStorage.getItem("password");
  const nav = useNavigate();

  // folder & pagination state
  const [folder,      setFolder]      = useState("");
  const [pages,       setPages]       = useState([]);         // array of pages, each page is array of keys
  const [cursors,     setCursors]     = useState([undefined]); // cursor for each page
  const [pageIndex,   setPageIndex]   = useState(0);
  const [hasMore,     setHasMore]     = useState(true);
  const [loadingPage, setLoadingPage] = useState(false);

  // upload state
  const [queue,      setQueue]      = useState([]);
  const [progress,   setProgress]   = useState({});
  const [error,      setError]      = useState("");
  const [modalIndex, setModalIndex] = useState(null);

  const prefix     = folder.trim() ? `${folder.trim()}/` : "";
  const fullPrefix = `uploads/${prefix}`;

  // pagination: load page `idx`
  const loadPage = useCallback(async idx => {
    if (loadingPage || pages[idx]) return;
    setLoadingPage(true);
    try {
      const params = new URLSearchParams({
        prefix: fullPrefix,
        limit:  PER_PAGE.toString(),
      });
      const cursor = cursors[idx];
      if (cursor) params.set("cursor", cursor);

      const res = await fetch(`${API_ROOT}/list?${params}`, {
        headers: { "X-Password": pw },
      });
      if (!res.ok) throw new Error(`List failed (${res.status})`);

      const { keys, cursor: nextCursor, truncated } = await res.json();
      setPages(p => {
        const np = [...p];
        np[idx] = keys.map(decodeURIComponent);
        return np;
      });
      setCursors(c => {
        const nc = [...c];
        nc[idx + 1] = nextCursor;
        return nc;
      });
      setHasMore(truncated);
    } catch (e) {
      console.error(e);
      setError("Failed to load page");
    } finally {
      setLoadingPage(false);
    }
  }, [pw, fullPrefix, pages, cursors, loadingPage]);

  // reset when folder changes
  useEffect(() => {
    setPages([]);
    setCursors([undefined]);
    setPageIndex(0);
    setHasMore(true);
  }, [fullPrefix]);

  useEffect(() => {
  window.scrollTo(0, 0);
}, [pageIndex, fullPrefix]);

  // always ensure current page is loaded
  useEffect(() => {
    loadPage(pageIndex);
  }, [pageIndex, loadPage]);

  // Track upload progress
  const updatePct = (origName, pct) =>
    setProgress(p => ({ ...p, [origName]: pct }));

  const withFolder = name => `${prefix}${name}`;

  // small direct upload
  const uploadSmall = async (file, origName) => {
    const path = withFolder(file.name);
    const res  = await fetch(
      `${API_ROOT}/upload?name=${encodeURIComponent(path)}`,
      { method: "PUT", headers: { "Content-Type": file.type, "X-Password": pw }, body: file }
    );
    if (!res.ok) throw new Error(`PUT failed (${res.status})`);
    const { key } = await res.json();
    updatePct(origName, 100);
    // prepend to current page
    setPages(p => {
      const np = [...p];
      np[pageIndex] = [key, ...(np[pageIndex]||[])];
      return np;
    });
  };

  // multipart upload
  const uploadLarge = async (file, origName) => {
    const path = withFolder(file.name);
    const initRes = await fetch(`${API_ROOT}/multipart/init`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Password": pw },
      body: JSON.stringify({ filename: path, type: file.type }),
    });
    if (!initRes.ok) throw new Error("init failed");
    const { key, uploadId } = await initRes.json();

    const total = Math.ceil(file.size / PART_SIZE);
    const parts = [];

    const sendPart = async n => {
      const start = (n - 1) * PART_SIZE;
      const blob  = file.slice(start, Math.min(start + PART_SIZE, file.size));
      for (let attempt = 1; attempt <= 3; attempt++) {
        const res = await fetch(
          `${API_ROOT}/multipart/part?key=${encodeURIComponent(key)}`,
          {
            method: "PUT",
            headers: {
              "X-Password": pw,
              "X-Upload-Id": uploadId,
              "X-Part-Number": n.toString(),
            },
            body: blob,
          }
        );
        if (res.ok) {
          const { etag } = await res.json();
          parts.push({ partNumber: n, etag });
          updatePct(origName, (parts.length / total) * 99);
          return;
        }
      }
      throw new Error(`part ${n} failed`);
    };

    for (let i = 1; i <= total; i++) {
      await sendPart(i);
    }

    parts.sort((a, b) => a.partNumber - b.partNumber);
    const compRes = await fetch(`${API_ROOT}/multipart/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Password": pw },
      body: JSON.stringify({ key, uploadId, parts }),
    });
    if (!compRes.ok) throw new Error("complete failed");
    updatePct(origName, 100);
    setPages(p => {
      const np = [...p];
      np[pageIndex] = [key, ...(np[pageIndex]||[])];
      return np;
    });
  };

  // compress + choose small/large
  const uploadFile = async originalFile => {
    try {
      const file = await compressImage(originalFile);
      if (file.size <= SINGLE_LIMIT) {
        await uploadSmall(file, originalFile.name);
      } else {
        await uploadLarge(file, originalFile.name);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Upload failed");
    }
  };

    const { getRootProps, getInputProps } = useDropzone({
    multiple: true,
    disabled: !folder.trim(), // disable until a folder name is entered
    onDrop: async files => {
      if (!pw) return nav("/");
      if (!folder.trim()) {
        setError("Prosim, najprej vnesite ime mape pred nalaganjem.");
        return;
      }
      setError("");
      setQueue(files);
      await Promise.all(files.map(uploadFile));
      window.location.reload();
    },
  });

  // derive current page’s gallery
  const gallery = pages[pageIndex] || [];

const folders = useMemo(() => {
  const map = {};
  gallery.forEach(k => {
    let rel = k.replace(/^uploads\//, "");
    if (!rel.startsWith(prefix)) return;
    rel = rel.slice(prefix.length);
    const slash = rel.indexOf("/");
    if (slash === -1) return;
    const folderName = rel.slice(0, slash);
    if (!map[folderName]) map[folderName] = { name: folderName, thumb: null };
    if (!map[folderName].thumb && imageExt.includes(getExt(k))) {
      map[folderName].thumb = `${API_ROOT}/file/${encodeURIComponent(k)}`;
    }
  });

  // Extract (DD.MM.YYYY) and sort by date, newest first
  const extractDate = name => {
    const match = name.match(/\((\d{1,2})\.(\d{1,2})\.(\d{4})\)/);
    if (!match) return new Date(0);
    // JS Date: yyyy, mm (0-based), dd
    return new Date(
      Number(match[3]),        // year
      Number(match[2]) - 1,    // month (0-based)
      Number(match[1])         // day
    );
  };

  return Object.values(map).sort(
    (a, b) => extractDate(b.name) - extractDate(a.name) // newest left
  );
}, [gallery, prefix]);

  // files at this level in this page
  const filesHere = useMemo(() => {
    return gallery
      .filter(k => !k.replace(/^uploads\//, "").slice(prefix.length).includes("/"))
      .sort((a, b) => {
        const oa =      imageExt.includes(getExt(a)) ? 0
                 : videoExt.includes(getExt(a)) ? 1
                 : audioExt.includes(getExt(a)) ? 2 : 3;
        const ob =      imageExt.includes(getExt(b)) ? 0
                 : videoExt.includes(getExt(b)) ? 1
                 : audioExt.includes(getExt(b)) ? 2 : 3;
        return oa - ob;
      });
  }, [gallery, prefix]);

  const openFolder = name => {
    const full = folder ? `${folder}/${name}` : name;
    nav(`/Onas/Podstrani/Login/Upload/GalleryHome?folder=${encodeURIComponent(full)}`);
  };
  const goBack = () =>
    setFolder(f => (f.includes("/") ? f.replace(/\/[^/]+$/, "") : ""));
  const handleLogout = () => {
    sessionStorage.removeItem("password");
    nav("/Onas/Podstrani/Login", { replace: true });
  };

  if (!pw) {
    return (
      <div className="upload-container">
        <p className="status-text">Not authorized</p>
        <button className="btn" onClick={() => nav("/")}>Go to Login</button>
      </div>
    );
  }

  return (
    <div className="upload-container">
      <center>
        <Link to="/Onas/Podstrani/Login/Upload">
          <img src="/Slike/rc.png" className="cloudlogorc" />
        </Link>
      </center>
      <img src="/Slike/code.gif" className="cloudgif" alt="Animated icon" />

      <p className="folder-title">{folder || "DODAJ MAPO :"}</p>
      {folder && <button className="btn back-btn" onClick={goBack}>← Nazaj</button>}
      <input
        className="dir-input"
        placeholder="Izberi ime mape…"
        value={folder}
        onChange={e => {
          const val = e.target.value.replace(/^\/+|\/+$/g, "");
          setFolder(val);
          if (val.trim()) setError("");
        }}
      />

      <div {...getRootProps()} className={`dropzone${!folder.trim() ? " disabled" : ""}`}>
        <input {...getInputProps()} />
        <p className="dropzonetext">
          {folder.trim() ? "Klikni ali spusti datoteke …" : "Vnesite ime mape zgoraj, da omogočite nalaganje."}
        </p>
      </div>

      {error && <p className="error-text">{error}</p>}

      <div className="progress-list-container">
        {queue.map(f => {
          const pct = progress[f.name] || 0;
          return (
            <div key={f.name} className="progressbarkon">
              <div className="progress-row">
                <span className="progress-label">{f.name}</span>
                <div className="progress-track">
                  <div className="progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <span className="progress-percent">{Math.round(pct)}%</span>
              </div>
            </div>
          );
        })}
      </div>

      {!!folders.length && (
        <>
          <p className="gallery-count">ŠTEVILO MAP: ({folders.length})</p>
          <div className="folder-grid">
            {folders.map(({ name, thumb }) => (
              <div key={name} className="folder-card" onClick={() => openFolder(name)}>
                <div className="folder-thumb">
                  {thumb ? <img src={thumb} alt={name} /> : <div className="no-thumb">▶</div>}
                </div>
                <div className="folder-name">{name}</div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="images-grid">
        {filesHere.map((key, idx) => {
          const label = key.replace(/^uploads\//, "").slice(prefix.length);
          const ext   = getExt(key);
          const url   = `${API_ROOT}/file/${encodeURIComponent(key)}`;

          if (imageExt.includes(ext)) {
            return (
              <div key={key} className="image-item" onClick={() => setModalIndex(idx)}>
                <img src={url} alt={label} />
              </div>
            );
          }
          if (videoExt.includes(ext)) {
            return (
              <div key={key} className="image-item video-item" onClick={() => setModalIndex(idx)}>
                  <video
                  src={url}
                  muted
                  preload="metadata"
                  playsInline
                />
                <span className="play-overlay">▶</span>
              </div>
            );
          }
          if (audioExt.includes(ext)) {
            return (
              <div key={key} className="image-item audio-item">
                <audio controls src={url} />
                <span className="file-label">{label}</span>
              </div>
            );
          }
          return (
            <div key={key} className="image-item other-item">
              <a href={url} target="_blank" rel="noreferrer">📄 {label}</a>
            </div>
          );
        })}
      </div>

      {modalIndex !== null && (
        <div className="modal-backdrop" onClick={() => setModalIndex(null)}>
          <button className="nav-btn" onClick={e => { e.stopPropagation(); setModalIndex(i => (i > 0 ? i - 1 : filesHere.length - 1)); }}>‹</button>
          <div className="modal-body" onClick={e => e.stopPropagation()}>
            {(() => {
              const key = filesHere[modalIndex];
              const ext = getExt(key);
              const url = `${API_ROOT}/file/${encodeURIComponent(key)}`;
              if (imageExt.includes(ext)) return <img src={url} alt="" />;
              if (videoExt.includes(ext)) return <video src={url} controls autoPlay />;
              if (audioExt.includes(ext)) return <audio src={url} controls autoPlay />;
              return <a href={url} className="download-link">Download</a>;
            })()}
          </div>
          <button className="nav-btn" onClick={e => { e.stopPropagation(); setModalIndex(i => (i < filesHere.length - 1 ? i + 1 : 0)); }}>›</button>
        </div>
      )}

<div className="pagination-controls">
  <button
    className="btn"
    onClick={() => setPageIndex(i => Math.max(0, i - 1))}
    disabled={pageIndex === 0 || loadingPage}
  >
    ← Prev
  </button>

  <span>Stran {pageIndex + 1}</span>

  <button
    className="btn"
    onClick={() => hasMore && setPageIndex(i => i + 1)}
    disabled={!hasMore || loadingPage}
  >
    Next →
  </button>
</div>

      <button className="btn logout-btn" onClick={handleLogout}>Odjavi se</button>
      <p className="cloudrtg">-</p>
    </div>
  );
}